import React from 'react'
import {
  Activity,
  Search,
  Filter,
  RefreshCw,
  Check,
  AlertCircle,
  Upload,
  Cpu,
  Edit3,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Info,
  Zap,
  LayoutDashboard,
  FileText,
  Files,
} from 'lucide-react'

// ─── Section Wrapper ─────────────────────────────────────────────────────────
function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ─── Mockup: Page Header ─────────────────────────────────────────────────────
function PageHeaderMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <ChevronLeft className="h-4 w-4 text-gray-500" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">Activity Log</h2>
            <p className="text-[10px] text-gray-500">248 total activities</p>
          </div>
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300">
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>
    </div>
  )
}

// ─── Mockup: Filters ─────────────────────────────────────────────────────────
function FiltersMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px] relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <div className="pl-8 pr-3 py-1.5 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-[10px] text-gray-400">
            Search activities...
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-[10px] text-gray-600 dark:text-gray-300">
          <Filter className="h-3 w-3" />
          All Activity
          <ChevronRight className="h-2.5 w-2.5 rotate-90" />
        </div>
      </div>
      <div className="flex gap-1.5 mt-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-[9px] text-blue-700 dark:text-blue-300 font-medium">
          Type: Published <span className="ml-0.5 cursor-pointer">&times;</span>
        </span>
      </div>
    </div>
  )
}

// ─── Mockup: Activity List ───────────────────────────────────────────────────
function ActivityListMockup() {
  const items = [
    { type: 'publish', icon: Check, color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400', title: 'Invoice_Q4.pdf published', time: '2 hours ago', badge: 'Published', badgeColor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
    { type: 'extraction', icon: Cpu, color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400', title: 'AI extraction completed for Bond_Terms.pdf', time: '3 hours ago', badge: 'Extraction', badgeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
    { type: 'upload', icon: Upload, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400', title: 'Contract_2024.pdf uploaded via email', time: '5 hours ago', badge: 'Upload', badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    { type: 'edit', icon: Edit3, color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400', title: 'John edited 3 fields on Request #a4f2c', time: '6 hours ago', badge: 'Edit', badgeColor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
    { type: 'cancel', icon: AlertCircle, color: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400', title: 'Request #b72d1 cancelled - Duplicate submission', time: 'Yesterday', badge: 'Cancelled', badgeColor: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
    { type: 'new_request', icon: Plus, color: 'bg-navy-100 dark:bg-navy-900/30 text-navy-600 dark:text-navy-400', title: 'New request created from outlook@company.com', time: 'Yesterday', badge: 'New Request', badgeColor: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  ]

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      {/* Date header */}
      <div className="px-4 py-1.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider">Today</p>
      </div>
      {items.slice(0, 4).map((item, i) => {
        const Icon = item.icon
        return (
          <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{item.title}</p>
              <p className="text-[10px] text-gray-500">{item.time}</p>
            </div>
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${item.badgeColor}`}>{item.badge}</span>
          </div>
        )
      })}
      {/* Date header */}
      <div className="px-4 py-1.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 border-t">
        <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider">Yesterday</p>
      </div>
      {items.slice(4).map((item, i) => {
        const Icon = item.icon
        return (
          <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{item.title}</p>
              <p className="text-[10px] text-gray-500">{item.time}</p>
            </div>
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${item.badgeColor}`}>{item.badge}</span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Mockup: Pagination ──────────────────────────────────────────────────────
function PaginationMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <p className="text-[10px] text-gray-500">Showing 1-25 of 248 results</p>
          <div className="flex items-center gap-1.5 text-[10px] text-gray-600 dark:text-gray-400">
            <span>Rows per page</span>
            <span className="px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700">25</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="px-2 py-0.5 text-[10px] text-gray-400 rounded">Previous</span>
          <span className="w-6 h-6 flex items-center justify-center text-[10px] rounded bg-navy-800 text-white font-medium">1</span>
          <span className="w-6 h-6 flex items-center justify-center text-[10px] rounded text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">2</span>
          <span className="w-6 h-6 flex items-center justify-center text-[10px] rounded text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">3</span>
          <span className="text-[10px] text-gray-400">...</span>
          <span className="w-6 h-6 flex items-center justify-center text-[10px] rounded text-gray-600 dark:text-gray-400">10</span>
          <span className="px-2 py-0.5 text-[10px] text-blue-600 rounded font-medium">Next</span>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Guide Component
// ═══════════════════════════════════════════════════════════════════════════════
export default function ActivityGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Activity Log</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Track all document processing events, user edits, and system actions across your organisation.</p>
        </div>
      </div>

      {/* Section 1: Page Header */}
      <Section number="1" title="Page Header">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          The header shows a back arrow (returns to Dashboard), the page title with total activity count, and a <strong>Refresh</strong> button to reload the latest entries.
        </p>
        <PageHeaderMockup />
      </Section>

      {/* Section 2: Search & Filtering */}
      <Section number="2" title="Search & Type Filter">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Use the search bar to find activities by keyword and the type dropdown to filter by event category.
        </p>
        <FiltersMockup />
        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Type filter options:</strong></p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { label: 'All Activity', desc: 'Show everything' },
              { label: 'Published', desc: 'Approved/exported requests' },
              { label: 'Cancelled', desc: 'Rejected or cancelled requests' },
              { label: 'Uploads', desc: 'Documents uploaded via email or manual' },
              { label: 'Extractions', desc: 'AI extraction completions' },
              { label: 'Edits', desc: 'Field changes by users' },
              { label: 'New Requests', desc: 'Newly created requests' },
            ].map((item) => (
              <div key={item.label} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                <p className="text-[10px] font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                <p className="text-[9px] text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
          <p>Active filters appear as removable pill badges below the search bar. Click the &times; to clear a filter.</p>
        </div>
      </Section>

      {/* Section 3: Activity List */}
      <Section number="3" title="Activity List">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Activities are grouped by date and displayed as a clickable list. Each entry shows a color-coded icon, description, relative timestamp, and a type badge.
        </p>
        <ActivityListMockup />
        <div className="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Activity types and icons:</strong></p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { icon: Check, color: 'text-green-600 bg-green-100', label: 'Published' },
              { icon: AlertCircle, color: 'text-red-600 bg-red-100', label: 'Cancelled' },
              { icon: Upload, color: 'text-blue-600 bg-blue-100', label: 'Upload' },
              { icon: Cpu, color: 'text-purple-600 bg-purple-100', label: 'Extraction' },
              { icon: Edit3, color: 'text-orange-600 bg-orange-100', label: 'Edit' },
              { icon: Plus, color: 'text-gray-700 bg-gray-100', label: 'New Request' },
            ].map((item) => {
              const Icon = item.icon
              return (
                <div key={item.label} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${item.color}`}>
                    <Icon className="h-2.5 w-2.5" />
                  </div>
                  <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                </div>
              )
            })}
          </div>
          <p><strong>Click behaviour:</strong> Upload entries navigate to the Document detail page. All other types navigate to the Request detail page.</p>
        </div>
      </Section>

      {/* Section 4: Pagination */}
      <Section number="4" title="Pagination">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Large activity logs are paginated. You can adjust rows per page (10, 25, 50) and navigate between pages.
        </p>
        <PaginationMockup />
      </Section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">Quick Navigation</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">Click any activity entry to jump directly to the related request or document for immediate context.</p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">Filter by Type</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">Use the type dropdown to quickly isolate specific events - e.g., filter by "Edits" to see all recent field changes across your team.</p>
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'requests', label: 'Requests', icon: FileText },
            { id: 'documents', label: 'Documents', icon: Files },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
